import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    getSortedRowModel,
    OnChangeFn,
    Row,
    RowSelectionState,
    SortingState,
    TableMeta,
    useReactTable,
    type VisibilityState,
} from "@tanstack/react-table";
import { Fragment, useMemo, useEffect } from "react";
import { Spinner } from "@heroui/react";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowDownWideShort,
    faArrowUpShortWide,
} from "@fortawesome/free-solid-svg-icons";

function ExpandingTable<TData>({
    meta,
    data,
    columns,
    sorting,
    setSorting,
    manualSorting,
    isLoading,
    onGlobalFilterChange,
    showScrollbar = false,
    onRowClick,
    pinnedColumns,
    columnVisibility,
    setColumnVisibility,
    paddingBottom,
    renderExpandedRow,
    enableMultiRowSelection = true,
    getIsRowExpanded,
    getRowId,
    onRowSelectionChange,
    onSelectionChange,
    rowSelection,
}: {
    meta?: TableMeta<TData>;
    data: TData[];
    columns: ColumnDef<TData>[];
    sorting: SortingState;
    setSorting: (
        updater: SortingState | ((old: SortingState) => SortingState),
    ) => void;
    manualSorting?: boolean;
    isLoading?: boolean;
    onSelectionChange?: (selectedData: TData[]) => void;
    onGlobalFilterChange?: (value: string) => void;
    showScrollbar?: boolean;
    onRowClick?: (row: TData) => void;
    pinnedColumns?: string[];
    columnVisibility?: Record<string, boolean>;
    setColumnVisibility?: (
        updater: VisibilityState | ((old: VisibilityState) => VisibilityState),
    ) => void;
    paddingBottom?: string;
    renderExpandedRow?: (toggleExpanded: () => void) => React.ReactNode;
    enableMultiRowSelection?: boolean;
    getIsRowExpanded?: (row: Row<TData>) => boolean;
    getRowId?: (row: TData) => string;
    onRowSelectionChange?: OnChangeFn<RowSelectionState>;
    rowSelection?: RowSelectionState;
}) {
    const table = useReactTable<TData>({
        columns,
        data: data,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        state: {
            sorting,
            columnVisibility: columnVisibility ?? {},
            ...(rowSelection ? { rowSelection } : {}),
        },
        onSortingChange: setSorting,
        onColumnVisibilityChange: setColumnVisibility,
        manualSorting: manualSorting || false,
        onGlobalFilterChange: onGlobalFilterChange,
        globalFilterFn: (row, columnId, filterValue) => {
            const value = row.getValue(columnId);
            return String(value)
                .toLowerCase()
                .includes(filterValue.toLowerCase());
        },
        meta: meta,
        initialState: {
            columnPinning: {
                left:
                    (pinnedColumns?.length ?? 0 > 0)
                        ? pinnedColumns
                        : undefined,
            },
        },
        getExpandedRowModel: getExpandedRowModel(),
        getIsRowExpanded,
        onRowSelectionChange,
        getRowId,
        enableMultiRowSelection,
    });

    const { rows } = table.getRowModel();
    const selectedRows = table.getSelectedRowModel();
    const selectedFields = useMemo(
        () => selectedRows.rows.map((row) => row.original),
        [selectedRows],
    );
    useEffect(() => {
        if (onSelectionChange) {
            onSelectionChange(selectedFields);
        }
    }, [selectedFields, onSelectionChange]);

    return (
        <div
            className={classNames(
                "h-full overflow-y-scroll overflow-x-scroll bg-white w-full",
                {
                    "scrollbar-hide": !showScrollbar,
                },
            )}
            style={{
                paddingBottom,
            }}
        >
            <div
                style={{
                    position: "relative",
                }}
            >
                {isLoading && (
                    <div className="absolute inset-0 bg-gray-100/60 z-20 w-full flex items-start justify-center">
                        <Spinner
                            size="lg"
                            color="primary"
                            label="Loading..."
                            className="opacity-100 pt-4"
                        />
                    </div>
                )}
                <div
                    className="sticky top-0 z-10 flex flex-col w-full bg-white border-b border-gray-200 min-w-fit"
                    role="rowgroup"
                >
                    {table.getHeaderGroups().map((group) => (
                        <div
                            className="flex flex-row items-center justify-start"
                            key={group.id}
                        >
                            {group.headers.map((header) => {
                                const isPinned = header.column.getIsPinned();
                                const pinnedStyles = isPinned
                                    ? {
                                          position: "sticky" as const,
                                          left: header.column.getStart("left"),
                                          zIndex: 11,
                                          backgroundColor: "white",
                                      }
                                    : {};

                                return (
                                    <div
                                        key={header.id}
                                        aria-label={
                                            header.column.columnDef.header?.toString() ??
                                            "column header"
                                        }
                                        style={{
                                            width: header.column.getSize(),
                                            minWidth: 0,
                                            ...pinnedStyles,
                                        }}
                                        className="py-1 font-extrabold"
                                    >
                                        {header.isPlaceholder ? null : (
                                            <div
                                                onClick={header.column.getToggleSortingHandler()}
                                                style={{
                                                    width: "100%",
                                                }}
                                                className={classNames({
                                                    "rounded-lg py-1 pr-1 text-start w-full flex items-center text-sm h-full":
                                                        true,
                                                    "cursor-pointer":
                                                        header.column.getCanSort(),
                                                })}
                                            >
                                                {flexRender(
                                                    header.column.columnDef
                                                        .header,
                                                    header.getContext(),
                                                )}
                                                {header.column.getCanSort() &&
                                                    header.column.getIsSorted() && (
                                                        <FontAwesomeIcon
                                                            className="ml-2 text-primary-500"
                                                            icon={
                                                                header.column.getIsSorted() ===
                                                                "asc"
                                                                    ? faArrowUpShortWide
                                                                    : faArrowDownWideShort
                                                            }
                                                            size="sm"
                                                        />
                                                    )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>

                <div className="relative min-w-fit">
                    {rows.map((row) => {
                        return (
                            <Fragment key={row.id}>
                                <div
                                    key={row.id}
                                    className={classNames(
                                        "border-b items-center border-gray-200",
                                        {
                                            "bg-gray-50 hover:bg-gray-100 cursor-pointer":
                                                onRowClick,
                                        },
                                    )}
                                    style={{
                                        minHeight: "40px    ",
                                        display: "flex",
                                    }}
                                    role="row"
                                    onClick={() => {
                                        if (onRowClick) {
                                            onRowClick(row.original);
                                        }
                                    }}
                                >
                                    {row.getVisibleCells().map((cell) => {
                                        const isPinned =
                                            cell.column.getIsPinned();
                                        const pinnedStyles = isPinned
                                            ? {
                                                  position: "sticky" as const,
                                                  left: cell.column.getStart(
                                                      "left",
                                                  ),
                                                  zIndex: 1,
                                              }
                                            : {};

                                        return (
                                            <div
                                                key={cell.id}
                                                style={{
                                                    width: cell.column.getSize(),
                                                    minWidth: 0,
                                                    overflow: "hidden",
                                                    ...pinnedStyles,
                                                }}
                                                className={classNames({
                                                    "bg-gray-50 hover:bg-gray-100":
                                                        isPinned && onRowClick,
                                                    "bg-white":
                                                        isPinned && !onRowClick,
                                                })}
                                                role="cell"
                                            >
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext(),
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                {renderExpandedRow &&
                                    row.getIsExpanded() &&
                                    renderExpandedRow(row.toggleExpanded)}
                            </Fragment>
                        );
                    })}

                    {data?.length == 0 && !isLoading && (
                        <div className="absolute h-[100px] translate-y-full w-full bottom-0 flex items-center justify-center z-40">
                            <p className="text-gray-500">No data found</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ExpandingTable;
